import {
  BodyCreateMessage,
  CollectionView,
  ConversationView,
  DmsView,
  FacebookPage,
  IGroup,
  MediaView,
  MetaPermission,
  RemoteConversationView,
  StoredConversationView,
  ShareMedia,
} from '..';
import {
  DisplayPreviewContentView,
  DisplayPreviewView,
  RightRequestView,
  UserView,
  CreatorView,
  GroupUserView,
  CRMTemplateView,
  BodyCreateMessages,
} from './views';

import { Media, Product, RightRequestStatus } from '@adalong/schemas';
import { MediaFacet, MediaQuery } from './api/media';
import { WidgetView } from './views/widget';
import { IWidget, ProductsLocalizationMapping } from './models';
import { ProductGroupView } from './views/productGroup';
import { CRMTemplateType } from './models/CRMTemplate';

export interface RightRequest {
  codeOfPost: string;
  groupName: string;
  source: string;
  status: string;
  lastUpdatedAt: Date;
  createdAt: Date;
  requestedBy: string;
  approbationTag: string;
  requestText: string;
  socialID?: string;
  iGMediaGraphID?: string;
  publishedAt?: Date;
  publishCommentID?: string;
  agreedAt?: Date;
  approbationCommentID?: string;
  deletedAt?: Date;
}

// influencers
export interface Creator {
  _id: string;
  username: string;
  groupName: string;
  countries?: string[];
  isInfluencer?: boolean;
  isBlacklisted?: boolean;
  isInfluencerDisabled?: boolean;
}

export type BodyCreateCreator = Omit<Creator, '_id' | 'groupName'> & {
  upsert?: boolean;
  countriesToExclude?: string[];
};
export type BodyCreateCreators = {
  creators: BodyCreateCreator[];
};
export type ParamsGetCreator = CreatorIdObject;
export interface CreatorIdObject {
  creatorId: string;
}
export type ResGetCreatorsList = CreatorView[];

export type ParamsDeleteInfluencer = CreatorIdObject;
export type ResDeleteInfluencer = boolean;

//TODO -- Update & Get

export type ParamsUpdateCreator = CreatorIdObject;
export interface BodyUpdateCreator {
  isInfluencer?: boolean;
  isBlacklisted?: boolean;
  countries?: string[];
  isInfluencerDisabled?: boolean;
}

// /collection
export type ResGetCollections = CollectionView[];

export type ResGetCollection = CollectionView;

export type ResCreateCollection = CollectionView;

export interface ParamsAddMediaToCollection {
  collectionId: string;
  mediaIds: string[];
}
export type ResAddMediaToCollection = CollectionView;

export interface ParamsRemoveMediaFromCollection {
  collectionId: string;
  mediaIds: string[];
}
export type ResRemoveMediaFromCollection = CollectionView;

export type ResSetCollectionUserPermissions = CollectionView;

export type ResDuplicateCollection = CollectionView;

export interface ResGetCollectionMedias {
  total?: number;
  medias: MediaView[];
  cursor?: string;
  facets?: {
    [key in MediaFacet]?: string[];
  };
}

// /group
export interface IRightsTemplate {
  template: IGroup['rights_template'];
  terms: string;
  fr: boolean;
}

export type ResGetRightsTemplate = Omit<IRightsTemplate, 'fr'>;

// product
export interface ParamsGetProducts {
  ids?: string[];
  productTypes?: string[];
  text?: string;
  cursor?: string;
  size?: number;
  localization?: string;
  externalIdsMatches?: string[];
}
export interface ResGetProducts {
  cursor?: string;
  products: Product[];
  total?: number;
}

export interface ParamsGetCatalogCountryProductIds {
  catalogCountry: string;
  cursor?: string;
  size?: number;
}

export interface ResGetCatalogCountryProductIds {
  cursor?: string;
  productIds: string[];
}

// CRMTemplates
export type ResGetTemplates = CRMTemplateView[];
export type ResCreateTemplate = CRMTemplateView;
export type ResUpdateTemplate = CRMTemplateView;
export type ResDeleteTemplate = { id: string };

export type BodyCreateCRMTemplate = {
  market?: string;
  language?: string;
  type: CRMTemplateType;
  name: string;
  url?: string;
  content: string;
  terms?: string;
  hashtag?: string;
};

export type BodyEditCRMTemplate = Partial<BodyCreateCRMTemplate> & {
  _id: string;
};

// it makes no sense to have a relevance-asc
export type SortOrder =
  | 'name-asc'
  | 'name-desc'
  | 'relevance-desc'
  | 'mediaCount-asc'
  | 'mediaCount-desc';
export const sortOrders: SortOrder[] = [
  'name-asc',
  'name-desc',
  'relevance-desc',
  'mediaCount-asc',
  'mediaCount-desc',
];

export interface ParamsGetProductGroups {
  productIds?: string[];
  productTypes?: string[];
  text?: string;
  cursor?: string;
  size?: number;
  catalogCountries?: string[];
  // this is formatted as per the catalong's API doc
  sortOrder?: SortOrder;
  withMedia?: boolean;
  applyLocalization?: boolean;
}
export interface ProductGroup {
  name: string;
  products: Product[];
  topMediaIds: string[];
  mediaCount: number;
}

export interface ResGetAllProductGroups {
  cursor?: string;
  productGroups: ProductGroup[];
  total?: number;
}

export interface ResGetProductGroups {
  cursor?: string;
  productGroups: ProductGroupView[];
  total?: number;
}

export interface ParamsPutObjectLabels {
  medias: {
    mediaId: string;
    productIdsExact?: string[];
    productIdsSimilar?: string[];
  }[];
}

export interface ParamsGetExternalIdsMappings {
  externalIds: string[];
}

export interface ResGetLocalizationMappings {
  res: ProductsLocalizationMapping[];
}

export interface ResGetExternalIdsMappings {
  res: Record<string, string>;
}

// rightsrequests
export interface ParamsPostRightsRequest {
  /**
   * Tag required by the content owner to
   * approve the RightsRequest
   */
  replyTag: string;
  /**
   * Text to send to the content owner
   */
  message: string;
  /**
   * Status of the rights request
   */
  status: RightRequestStatus;
}
export type ResPostRightsRequest = RightRequestView[];

export type ResGetRightsRequest = RightRequest[];

export type ResPutRightsRequest = RightRequestView[];

export type ParamsDeleteRightsRequest = void;
export type ResDeleteRightsRequest = Media['_id'][];

// /share
export type ResGetShareToken = string;

export type ResGetGroupUsers = GroupUserView[];

export type ResGetShareCollectionMedias = ShareMedia[];

// /user
export type ResGetFacebookPages = FacebookPage[];
export interface ParamsSwitchGroup {
  groupId: string;
}

// User Meta permissions
export type ResGetMetaPermissions = MetaPermission[];

export interface ResGetMe {
  admin?: {
    _id: string;
    username: string;
  };
  user: UserView & {
    groupFeatures: IGroup['feature_flags'];
    groupFeatureDisabler: IGroup['feature_disabler_flag'];
  };
  config: {
    url?: string | undefined;
    googleanalytics?: string | undefined;
    segment?: string | undefined;
    rightstemplate: {
      maxComment: number;
      commentReply: string;
      link: string;
    };
  };
}

// widget template

// Public routes
export type QueryGetWidgetContent = QueryWidgetPublicApi & {
  settings: string;
  products?: string;
  collections?: string;
};

export type QueryGetWidgetSettings = QueryWidgetPublicApi;

export type ResGetWidgetSettings = (IWidgetSettingsV6 | IWidgetSettingsV5) & { id: string };

export type IWidgetSettingsV6 = IWidget['settings'];

export interface IWidgetSettingsV5 {
  type: 'carousel' | 'wall' | 'mosaic';
  content_size: number;
  rights_agreed: boolean;
  diffusion: boolean;
  random: boolean;
  display_name: boolean;

  has_exact_products: boolean;
  min_exact_products: number;
  max_exact_products: number | null;

  has_similar_products: boolean;
  min_similar_products: number;
  max_similar_products: number | null;

  shop_this_look: boolean;

  default_sources?: {
    collection_list?: string[];
    collection_namelist?: string[];
    product_list?: string[];
    product_namelist?: string[];
  };

  localization?: string;
}

// Private routes
export type ParamsGetWidget = WidgetIdObject & { localization?: string };

export interface BodyCreateWidget {
  widget: {
    name: string;
    description: string;
    sourceType: 'collection' | 'product';
  };
}
export type ParamsUpdateWidget = WidgetIdObject;
export interface BodyUpdateWidget {
  doc: WidgetUpdate;
}

export type ParamsDeleteWidget = WidgetIdObject;

export type ResGetWidgetList = ResGetWidgetWithStatus[];
export type ResGetWidgetWithStatus = WidgetView & { active?: boolean };

export type ResGetWidget = WidgetView;

export type ResCreateWidget = WidgetView;

export type ResUpdateWidget = WidgetView;

export type ResDeleteWidget = boolean;

export interface QueryWidgetPublicApi {
  apiKey: string;
  widgetVersion?: number;
  localization?: string;
}
export interface WidgetIdObject {
  widgetId: string;
}

export interface WidgetUpdate {
  name: string;
  description: string;
  settings: WidgetView['settings'];
  is_locked?: boolean;
  is_deleted?: boolean;
}

// displaypreview
export type ResGetDisplayPreview = DisplayPreviewView;

export type ResMoveDisplayPreview = DisplayPreviewView;

export type ResInsertDisplayPreview = DisplayPreviewContentView;

export type ResRemoveDisplayPreviewItem = DisplayPreviewView;

export type ResSaveDraftDisplayPreview = DisplayPreviewView;

export interface FiltersParamGetCollectionMedias {
  limit?: number;
  cursor?: string;
  rightsStatus?: string;
}

export interface ParamsSearchCollectionMedias {
  collectionId: string;
  query: MediaQuery;
  token?: string;
}

// DMs & Conversations
export type ResGetConversations = ConversationView;

export type ResGetStoredConversations = StoredConversationView;

export type ResGetRemoteConversations = RemoteConversationView;

export type ResGetDms = DmsView;

export interface ParamsGetConversationMessages {
  interlocutorId: string;
  targetAccountId: string;
  network: string;
  cursor?: string;
  conversationId: string;
}

export type ParamsSendDm = BodyCreateMessage;

export type ParamsSendDms = BodyCreateMessages;

export interface ParamsMarkConversationAsRead {
  networkName: string;
  networkSendersId: string;
  networkReceiverId: string;
}

export type ResMarkConversationAsRead = { status: string };

export interface ParamsToggleConversationFavorite {
  networkName: string;
  networkSendersId: string;
  networkReceiverId: string;
  isFavorite: boolean;
}

export type ResToggleConversationFavorite = { status: string };

export interface Analytics {
  groupName: string;
  date: string;
  month: string;
  year: string;
  week: string;
  ingestion: {
    videosCounts: Record<string, number>;
    imagesCounts: Record<string, number>;
  };
  language: Record<string, number>;
  rightsRequests: {
    agreed: number;
    requested: number;
  };
}
export interface ResGetAnalytics {
  res: Analytics[];
}

/**
 * `ugc` is kept for backwards compatibility with the initial social dashboard.
 * The four section keys allow the client to load a lighter dedicated dashboard
 * for the active UGC section.
 */
export type MetabaseAnalyticsDashboard =
  | 'ecom'
  | 'ugc'
  | 'ugc_brand'
  | 'ugc_community'
  | 'ugc_rights'
  | 'ugc_commerce';

export type AnalyticsUnit = 'week' | 'month' | 'quarter' | 'year';
export type AnalyticsTimeframe = number | 'all';

export interface AnalyticsResolvedPeriod {
  start: string;
  end: string;
  label: string;
}

export interface AnalyticsResolvedPeriods {
  unit: AnalyticsUnit;
  timeframe: AnalyticsTimeframe;
  kpi: AnalyticsResolvedPeriod;
  comparison: AnalyticsResolvedPeriod | null;
  charts: {
    start: string | null;
    end: string;
  };
}

export interface ParamsGetMetabaseEmbed {
  dashboard: MetabaseAnalyticsDashboard;
  unit: AnalyticsUnit;
  timeframe: AnalyticsTimeframe;
}

export interface ResGetMetabaseEmbed {
  dashboardId: number;
  /** Short-lived Guest JWT; never persist or expose it outside memory. */
  token: string;
  /** Unix timestamp in seconds at which `token` expires. */
  expiresAt: number;
  /** Editable Metabase filters only; locked filters such as `group_name` are in the JWT. */
  parameters: Record<string, unknown>;
  /** Server-resolved periods used for labels and debug attributes in the client. */
  periods: AnalyticsResolvedPeriods;
}

export interface ParamsGetSocialTopProducts {
  from: string;
  to: string;
  limit?: number;
}

export interface TopSocialProduct {
  productId: string;
  title: string;
  description: string | null;
  imageLink: string | null;
  ugcCount: number;
}

export interface ResGetSocialTopProducts {
  products: TopSocialProduct[];
  shoppableMediaCount: number;
}

export interface ParamsGetSocialTopProducts {
  from: string;
  to: string;
  limit?: number;
}

export interface TopSocialProduct {
  productId: string;
  title: string;
  description: string | null;
  imageLink: string | null;
  ugcCount: number;
}

export interface ResGetSocialTopProducts {
  products: TopSocialProduct[];
  shoppableMediaCount: number;
}

export interface ParamsGetProductsSuggestions {
  fileName: string;
}

export interface ProductSuggestion {
  name: string;
  products: Product[];
}

export interface ResGetProductsSuggestions {
  suggestions: ProductSuggestion[];
  hasEmbeddings: boolean;
}

export interface ParamsPostMediaDescribe {
  fullIds: string[];
}

export type MediaDescribeStatus =
  | 'generated'
  | 'already_exists'
  | 'not_found'
  | 'error'
  | 'invalid_request';

export interface MediaDescribeResult {
  fullId: string;
  status: MediaDescribeStatus;
  error?: string;
}

export interface ResPostMediaDescribe {
  results: MediaDescribeResult[];
}

export interface ReqSearchByEmbedding {
  groupName: string;
  embedding: number[];
  size: number;
}

export interface ResSearchByEmbedding {
  products: Product[];
}
